import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';

// Import views
import Login from '@/views/Login.vue';
import MobileLogin from '@/views/MobileLogin.vue';
import MobileSignup from '@/views/MobileSignup.vue';
import MobileOTPVerification from '@/views/MobileOTPVerification.vue';
import Dashboard from '@/views/Dashboard.vue';
import Trips from '@/views/Trips.vue';
import TripForm from '@/views/TripForm.vue';
import Reviews from '@/views/Reviews.vue';
import ReviewForm from '@/views/ReviewForm.vue';
import Users from '@/views/Users.vue';
import PublicTrips from '@/views/PublicTrips.vue';
import PublicTripDetail from '@/views/PublicTripDetail.vue';

const routes = [
  {
    path: '/',
    redirect: '/trips'
  },
  // Public Routes (for normal users)
  {
    path: '/trips',
    name: 'PublicTrips',
    component: PublicTrips,
    meta: {
      requiresAuth: false,
      title: 'Discover Amazing Destinations - Gypsy Souls'
    }
  },
  {
    path: '/trip/:id',
    name: 'PublicTripDetail',
    component: PublicTripDetail,
    props: true,
    meta: {
      requiresAuth: true,
      title: 'Trip Details - Gypsy Souls'
    }
  },

  {
    path: '/login',
    name: 'MobileLogin',
    component: MobileLogin,
    meta: {
      requiresGuest: true,
      title: 'Login - Gypsy Souls'
    }
  },
  {
    path: '/signup',
    name: 'MobileSignup',
    component: MobileSignup,
    meta: {
      requiresGuest: true,
      title: 'Sign Up - Gypsy Souls'
    }
  },
  {
    path: '/verify-otp',
    name: 'MobileOTPVerification',
    component: MobileOTPVerification,
    meta: {
      requiresGuest: true,
      title: 'Verify OTP - Gypsy Souls'
    }
  },
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: Login,
    meta: {
      requiresGuest: true,
      title: 'Admin Login - Gypsy Souls'
    }
  },
  // Admin Routes (protected)
  {
    path: '/admin',
    redirect: '/admin/dashboard'
  },
  {
    path: '/admin/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: {
      requiresAuth: true,
      title: 'Dashboard - Gypsy Souls Admin'
    }
  },
  {
    path: '/admin/trips',
    name: 'AdminTrips',
    component: Trips,
    meta: {
      requiresAuth: true,
      title: 'Manage Trips - Gypsy Souls Admin'
    }
  },
  {
    path: '/admin/trips/create',
    name: 'CreateTrip',
    component: TripForm,
    meta: {
      requiresAuth: true,
      title: 'Create Trip - Gypsy Souls Admin'
    }
  },
  {
    path: '/admin/trips/:id/edit',
    name: 'EditTrip',
    component: TripForm,
    props: true,
    meta: {
      requiresAuth: true,
      title: 'Edit Trip - Gypsy Souls Admin'
    }
  },
  {
    path: '/admin/reviews',
    name: 'Reviews',
    component: Reviews,
    meta: {
      requiresAuth: true,
      title: 'Manage Reviews - Gypsy Souls Admin'
    }
  },
  {
    path: '/admin/reviews/create',
    name: 'CreateReview',
    component: ReviewForm,
    meta: {
      requiresAuth: true,
      title: 'Create Review - Gypsy Souls Admin'
    }
  },
  {
    path: '/admin/reviews/:id/edit',
    name: 'EditReview',
    component: ReviewForm,
    props: true,
    meta: {
      requiresAuth: true,
      title: 'Edit Review - Gypsy Souls Admin'
    }
  },
  {
    path: '/admin/users',
    name: 'Users',
    component: Users,
    meta: {
      requiresAuth: true,
      title: 'User Management - Gypsy Souls Admin'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/login'
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  }
});

// Global navigation guards
router.beforeEach(async (to, from, next) => {
  // Set page title
  document.title = to.meta.title || 'Gypsy Souls Admin';

  // Define public routes that don't require authentication
  const publicRoutes = ['/', '/login', '/signup', '/verify-otp', '/admin/login'];
  const isPublicRoute = publicRoutes.includes(to.path);

  // For public routes, skip authentication checks entirely
  if (isPublicRoute) {
    next();
    return;
  }

  // Initialize auth state if not already done
  if (!store.getters['auth/isAuthenticated'] && localStorage.getItem('authToken')) {
    await store.dispatch('auth/initializeAuth');
  }

  const isAuthenticated = store.getters['auth/isAuthenticated'];
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth === true);
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest);

  if (requiresAuth && !isAuthenticated) {
    // Redirect to appropriate login based on route
    if (to.path.startsWith('/admin')) {
      next('/admin/login');
    } else {
      next('/login');
    }
  } else if (requiresGuest && isAuthenticated) {
    // Redirect based on user type
    const userType = store.getters['auth/userType'];
    if (userType === 'admin') {
      next('/admin/dashboard');
    } else {
      next('/trips');
    }
  } else {
    next();
  }
});

export default router;
